import React, { useMemo } from "react";
import { Checkbox, Link, Typography } from "@material-ui/core";
import { OptionValue } from "../types";
import { FormattedDate, FormattedNumber } from "react-intl";
import { isDate, isNumeric } from "../utils";
import { FieldType, ColumnMetadata } from "../tableSchema";
import {
	DployTextField,
	NumberField,
	SelectField,
	CheckboxWithLabelField,
	CurrencyField,
	PercentField,
	KeyboardDatePickerField
} from "@ploy-ui/form-fields";
import { FormikProps, useField } from "formik";
import {
	ErrorDisplay,
	TableColumnOptionSource,
	TemplateTableColumn
} from "@ploy-lib/types";
import { Row } from "react-table";

const getItemValue = (item: any, optionSource?: TableColumnOptionSource) => {
	let keyLabelToReturn: any;
	if (typeof item === "object") {
		keyLabelToReturn = item?.key
			? item.key
			: item?.[optionSource?.key ?? "key"];
	} else {
		keyLabelToReturn = item;
	}
	return keyLabelToReturn?.toString();
};

const getItemLabel = (item: any, optionSource?: TableColumnOptionSource) => {
	let itemValueToReturn: any;
	if (typeof item === "object") {
		itemValueToReturn = item?.value
			? item.value
			: item?.[optionSource?.value ?? "value"];
	} else {
		itemValueToReturn = item;
	}
	return itemValueToReturn?.toString();
};

const isValidNumber = (value: any) => {
	return value !== "" && value !== undefined && value !== null && !isNaN(value);
};

export const SimpleTextLiteral = <TRow extends {} = {}>(props: {
	value: any | any[];
	items?: OptionValue[];
	multiple?: boolean;
	hrefLink?: string;
	fieldMetadata?: ColumnMetadata<TRow>;
	tc: TemplateTableColumn;
}) => {
	const { value, items, multiple, hrefLink, fieldMetadata, tc, ...rest } =
		props;
	let valueForDisplay = value;

	if (hrefLink) {
		return (
			<Typography
				variant="subtitle2"
				style={{
					fontWeight: "inherit",
					width: props.tc.width ?? "100%"
				}}
				data-cr-component="LinkLiteral"
				{...rest}
			>
				<Link href={hrefLink} underline="hover">
					{valueForDisplay}
				</Link>
			</Typography>
		);
	}
	if (fieldMetadata) {
		return <SchemaField {...props} />;
	}

	if (multiple) {
		valueForDisplay = Array.isArray(value)
			? value
					?.map(v => {
						const item = items?.find(
							x => getItemValue!(x) === getItemValue!(v)
						);
						return item && getItemLabel ? getItemLabel(item) : getItemLabel!(v);
					})
					.join(",\n")
			: value;
	} else if ((items?.length ?? 0) > 0) {
		const item = items?.find(x => getItemValue!(x) === getItemValue!(value));
		valueForDisplay = item ? getItemLabel!(item) : value;
	} else if (isNumeric(value)) {
		const numericFieldValue = Number(value);
		return (
			<Typography
				variant="subtitle2"
				style={{
					fontWeight: "inherit",
					textAlign: "right",
					width: props.tc.width ?? "100%"
				}}
				data-cr-component="NumberLiteral"
				{...rest}
			>
				<FormattedNumber value={numericFieldValue} />
			</Typography>
		);
	} else if (isDate(value)) {
		const dateFieldValue = new Date(value.toString());
		return (
			<Typography
				variant="subtitle2"
				style={{
					fontWeight: "inherit",
					textAlign: "right",
					width: props.tc.width ?? "100%"
				}}
				data-cr-component="DateLiteral"
				{...rest}
			>
				<FormattedDate value={dateFieldValue} format="short_numeric" />
			</Typography>
		);
	}

	return (
		<Typography
			variant="subtitle2"
			style={{
				fontWeight: "inherit",
				width: props.tc.width ?? "100%",
				whiteSpace: multiple ? "pre-wrap" : "normal"
			}}
			data-cr-component="StringLiteral"
			{...rest}
		>
			{valueForDisplay}
		</Typography>
	);
};

// Select,
// Multiselect,
// Text,
// Number,
// Currency,
// Percent
const SchemaField = <TRow extends {} = {}>(props: {
	value?: any | any[];
	multiple?: boolean;
	fieldMetadata?: ColumnMetadata<TRow>;
	items?: OptionValue[];
	tc: TemplateTableColumn;
}) => {
	const {
		value,
		multiple,
		items,
		fieldMetadata = {} as ColumnMetadata,
		tc,
		...rest
	} = props;
	const dataCrComponent = FieldType[fieldMetadata.fieldType] + "Literal";
	const isMultiline = fieldMetadata.isMultiline ?? multiple;

	switch (fieldMetadata.fieldType) {
		case FieldType.Percent:
			return (
				<Typography
					variant="subtitle2"
					style={{
						fontWeight: "inherit",
						textAlign: "right",
						width: props.tc.width ?? "100%"
					}}
					data-cr-component={dataCrComponent}
					{...rest}
				>
					{isValidNumber(value) ? (
						<FormattedNumber
							value={value / 100}
							format={"percent"}
							maximumFractionDigits={fieldMetadata.maximumFractionDigits ?? 2}
							minimumFractionDigits={fieldMetadata.minimumFractionDigits ?? 2}
						/>
					) : (
						""
					)}
				</Typography>
			);
		case FieldType.Currency:
		case FieldType.Number:
			//Currency is currently identical to number, but there exists posibilities format currencies differently in the future
			return (
				<Typography
					variant="subtitle2"
					style={{
						fontWeight: "inherit",
						textAlign: "right",
						width: props.tc.width ?? "100%"
					}}
					data-cr-component={dataCrComponent}
					{...rest}
				>
					{isValidNumber(value) ? (
						<FormattedNumber
							value={value}
							maximumFractionDigits={fieldMetadata.maximumFractionDigits ?? 0}
							minimumFractionDigits={fieldMetadata.minimumFractionDigits ?? 0}
						/>
					) : (
						`${value === "-" ? "-" : ""}` // Support displaying a "N/A"-like value instead of empty cell for columns where this might be applicable
					)}
				</Typography>
			);
		case FieldType.Multiselect:
			const multiValue = Array.isArray(value)
				? value
						?.map(v => {
							const item = items?.find(
								x => getItemValue!(x) === getItemValue!(v)
							);
							return item && getItemLabel
								? getItemLabel(item)
								: getItemLabel!(v);
						})
						.join(",\n")
				: value;
			return (
				<Typography
					variant="subtitle2"
					style={{
						fontWeight: "inherit",
						width: props.tc.width ?? "100%",
						whiteSpace: isMultiline ? "pre-wrap" : "normal"
					}}
					data-cr-component={dataCrComponent}
					{...rest}
				>
					{multiValue}
				</Typography>
			);
		case FieldType.Select:
			const item = items?.find(x => getItemValue!(x) === getItemValue!(value));
			const selectValue = item ? getItemLabel!(item) : value;
			return (
				<Typography
					variant="subtitle2"
					style={{
						fontWeight: "inherit",
						width: props.tc.width ?? "100%",
						whiteSpace: isMultiline ? "pre-wrap" : "normal"
					}}
					data-cr-component={dataCrComponent}
					{...rest}
				>
					{selectValue}
				</Typography>
			);
		case FieldType.Date:
			if (!isDate(value)) return null;
			const dateFieldValue = new Date(value.toString());
			return (
				<Typography
					variant="subtitle2"
					style={{
						fontWeight: "inherit",
						textAlign: "right",
						width: props.tc.width ?? "100%"
					}}
					data-cr-component={dataCrComponent}
					{...rest}
				>
					<FormattedDate value={dateFieldValue} format="short_numeric" />
				</Typography>
			);
		case FieldType.Checkbox:
			return <Checkbox checked={value} disabled />;
		default:
			return (
				<Typography
					variant="subtitle2"
					style={{
						fontWeight: "inherit",
						width: props.tc.width ?? "100%",
						whiteSpace: isMultiline ? "pre-wrap" : "normal"
					}}
					data-cr-component="StringLiteral"
					{...rest}
				>
					{value}
				</Typography>
			);
	}
};

export const EditingSchemaField = <TRow extends {} = {}>(props: {
	fieldId: string;
	form: FormikProps<any>;
	fieldMetadata?: ColumnMetadata<TRow>;
	tc: TemplateTableColumn;
	tableData: TRow[];
	row: Row<TRow>;
}) => {
	const { fieldId, form, fieldMetadata, tc, row, tableData } = props;
	const [field, meta] = useField(`${fieldId}`);
	const error = !!form?.errors?.[fieldId];

	const isDisabled = useMemo(() => {
		// TableFields will only be marked disabled if they have a metadata-editableFunction that returns `false`
		const hasIsEditableFunction = fieldMetadata?.isEditableFunctions;
		if (hasIsEditableFunction) {
			const func = Object.values(fieldMetadata.isEditableFunctions)[0];
			const isEditable = func(form, row.cells, tableData);
			return !isEditable;
		}
		return false;
	}, [fieldMetadata?.isEditableFunctions, form, row.cells, tableData]);

	if (!field) return null;

	switch (fieldMetadata?.fieldType) {
		case FieldType.Currency:
		case FieldType.Percent:
		case FieldType.Number:
			let Component = NumberField;
			let formatString: string | undefined = "#,###";
			if (fieldMetadata.fieldType === FieldType.Percent) {
				Component = PercentField;
				formatString = undefined;
			}
			if (fieldMetadata.fieldType === FieldType.Currency)
				Component = CurrencyField;
			return (
				<Component
					formatString={formatString}
					data-cr-field={field.name}
					data-cr-component={"input"}
					disabled={isDisabled}
					variant="standard"
					size="small"
					field={field}
					form={form}
					meta={meta}
					cssStyle={{
						fontWeight: "inherit",
						textAlign: "right",
						width: props.tc.width ?? "100%"
					}}
					emptyEqualsZero={
						fieldMetadata?.fieldType !== FieldType.Percent &&
						fieldMetadata?.fieldType !== FieldType.Number
					}
					fullWidth
					error={error}
					allowNegative={fieldMetadata?.allowNegative}
					maximumFractionDigits={
						fieldMetadata?.maximumFractionDigits ??
						(fieldMetadata?.fieldType === FieldType.Percent ? 2 : 0)
					}
					errorDisplay={ErrorDisplay.AlwaysNoMessage}
					onBlur={e => fieldMetadata?.alwaysInEditMode && form.submitForm()}
				/>
			);
		case FieldType.Checkbox:
			return (
				<CheckboxWithLabelField
					data-cr-field={field.name}
					data-cr-component={"CheckboxField"}
					style={{
						width: props.tc.width ?? "100%"
					}}
					disabled={isDisabled}
					field={field}
					form={form}
					meta={meta}
					error={error}
					errorDisplay={ErrorDisplay.AlwaysNoMessage}
					onChange={(e, checked) => {
						form.setFieldValue(field.name, checked);
						fieldMetadata?.alwaysInEditMode && form.submitForm();
					}}
				/>
			);
		case FieldType.Select:
		case FieldType.Multiselect:
			const multipleSelect =
				fieldMetadata?.fieldType === FieldType.Multiselect ||
				tc.isMultipleSelect;
			return (
				<SelectField
					data-cr-field={field.name}
					data-cr-component={"SelectionField"}
					style={{
						width: props.tc.width ?? "100%"
					}}
					disabled={isDisabled}
					variant="standard"
					size="small"
					field={field}
					form={form}
					meta={meta}
					error={error}
					errorDisplay={ErrorDisplay.AlwaysNoMessage}
					disableClearable={multipleSelect}
					searchable
					multiple={multipleSelect}
					items={
						tc.optionValues
							?.filter(item => item.key || item.value)
							.sort((a, b) => a.value.localeCompare(b.value)) || []
					}
					getItemLabel={(item?: any) => getItemLabel(item, tc.optionSource)}
					getItemValue={(item?: any) => getItemValue(item, tc.optionSource)}
					fullWidth
					minWidthFocused={200}
				/>
			);
		case FieldType.Date:
			return (
				<KeyboardDatePickerField
					data-cr-field={field.name}
					data-cr-component="KeyboardDatePickerField"
					style={{
						width: props.tc.width ?? "100%"
					}}
					disabled={isDisabled}
					pickerVariant="dialog"
					variant="standard"
					form={form}
					field={field}
					meta={meta}
					error={error}
					errorDisplay={ErrorDisplay.AlwaysNoMessage}
					clearable
					allowNull={!Boolean(fieldMetadata.isRequired)}
					fullWidth
					onChange={(
						event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
					) => {
						form.setFieldValue(field.name, event.target.value);
					}}
				/>
			);
		default:
			return (
				<DployTextField
					data-cr-field={field.name}
					data-cr-component={"TextField"}
					style={{
						width: props.tc.width ?? "100%"
					}}
					disabled={isDisabled}
					variant="standard"
					size="small"
					{...field}
					fullWidth
					error={error}
				/>
			);
	}
};
